/* eslint-disable react/react-in-jsx-scope */
/*
 * File: ProductsOverviewTable.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 21 Jan 2022 2:53:02 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { CheckCircleFilled, InfoCircleOutlined, StopOutlined } from '@ant-design/icons';
import { Popover, Row, Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useFormikContext } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { useLocation } from 'react-router-dom';

const styles: InlineStylesModel = {
  table: {
    width: '100%'
  },
  number: {
    textAlign: 'right',
    border: 'none'
  }
};

export const ProductsOverviewTable = (): JSX.Element => {
  const { values } = useFormikContext<SalesOrderPayloadModel>();

  const { pathname } = useLocation();
  const isPosted = pathname.includes('/posted');

  /* ****************** Functions ****************** */
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  });

  const numberFormatter = new Intl.NumberFormat('en-US', {
    style: 'decimal'
  });

  /* ****************** JSX  / Variables ****************** */
  // const dataSource = lineItems

  /* ****************** Columns Data ****************** */
  const columns: ColumnsType<SalesOrderPayloadModel['lines'][number]> = [
    {
      title: 'Cust #',
      dataIndex: 'customerLineNumber',
      fixed: 'left',
      width: 20,
      render: (text: string, record): JSX.Element => {
        return <div>{record.customerLineNumber ?? 'N/A'}</div>;
      }
    },
    {
      title: 'Product Number',
      dataIndex: 'productNumber',
      width: 20,
      fixed: 'left',
      render: (text: string, record): JSX.Element => {
        return (
          <Space>
            <Popover
              content={
                <Space direction="vertical">
                  <div>Manufacturer: {record.manufacturer !== '' ? record.manufacturer : 'N/A'}</div>
                  <div>Category: {record.category !== '' ? record.category : 'N/A'}</div>
                </Space>
              }>
              <InfoCircleOutlined style={{ color: '#2786fa' }} />
            </Popover>
            {record.description === '' ? (
              <Popover content={'No description found'}>
                <div>{text}</div>
              </Popover>
            ) : (
              <Popover content={record.description}>
                <div>{text}</div>
              </Popover>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Owner::Condition::Disposition',
      dataIndex: 'ocd',
      width: 30,
      render: (_: string, record): JSX.Element => {
        return (
          <div>
            {record.ownerId} :: {record.conditionId} :: {record.dispositionId}
          </div>
        );
      }
    },
    {
      title: 'Site',
      dataIndex: 'siteId',
      width: 20,
      render: (text: string): JSX.Element => {
        return <div>{text}</div>;
      }
    },
    {
      title: 'Warehouse',
      dataIndex: 'warehouseId',
      width: 20,
      render: (text: string): JSX.Element => {
        return <div>{text}</div>;
      }
    },
    {
      title: 'Quantity',
      dataIndex: 'qty',
      width: 20,
      align: 'right',
      render: (text: string, record): JSX.Element => {
        return <div style={styles.number}>{numberFormatter.format(Number(record.quantity))}</div>;
      }
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      width: 20,
      align: 'right',
      render: (text: string, record): JSX.Element => {
        return <div style={styles.number}>{currencyFormatter.format(Number(record.unitPrice))}</div>;
      }
    },
    {
      title: 'Ext. Price',
      dataIndex: 'extendedPrice',
      width: 20,
      align: 'right',
      render: (text: string, record): JSX.Element => {
        return <div style={styles.number}>{currencyFormatter.format(Number(record.quantity) * Number(record.unitPrice))}</div>;
      }
    },
    {
      title: 'Delivery Type',
      dataIndex: 'deliveryType',
      width: 20,
      render: (text: string): JSX.Element => {
        return <div>{text}</div>;
      }
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor',
      width: 20,
      render: (text: string, record): JSX.Element => {
        return <div>{record.vendorId}</div>;
      }
    },
    {
      title: 'Unit Cost',
      dataIndex: 'unitCost',
      width: 20,
      align: 'right',
      render: (text: string, record): JSX.Element => {
        return <div style={styles.number}>{currencyFormatter.format(Number(record.unitCost))}</div>;
      }
    },
    {
      title: 'Total Cost',
      dataIndex: 'totalCost',
      width: 20,
      align: 'right',
      fixed: 'right',
      render: (text: string, record): JSX.Element => {
        return <div style={styles.number}>{currencyFormatter.format(Number(record.quantity) * Number(record.unitCost))}</div>;
      }
    }
  ];
  const columnsPosted: ColumnsType<SalesOrderPayloadModel['lines'][number]> = [
    {
      title: 'Cust #',
      dataIndex: 'customerLineNumber',
      width: 20,
      fixed: 'left',
      render: (text: string, record): JSX.Element => {
        return <div>{record.customerLineNumber ?? 'N/A'}</div>;
      }
    },
    {
      title: 'Product Number',
      dataIndex: 'productNumber',
      width: 20,
      fixed: 'left',
      render: (text: string, record): JSX.Element => {
        return (
          <Space>
            <Popover
              content={
                <Space direction="vertical">
                  <div>Manufacturer: {record.manufacturer !== '' ? record.manufacturer : 'N/A'}</div>
                  <div>Category: {record.category !== '' ? record.category : 'N/A'}</div>
                </Space>
              }>
              <InfoCircleOutlined style={{ color: '#2786fa' }} />
            </Popover>
            {record.description === '' ? (
              <Popover content={'No description found'}>
                <div>{text}</div>
              </Popover>
            ) : (
              <Popover content={record.description}>
                <div>{text}</div>
              </Popover>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Posted',
      dataIndex: 'isPosted',
      width: 10,
      render: (text: string, record): JSX.Element => {
        return (
          <Row justify="start" align="middle">
            {record.isPosted ? <CheckCircleFilled style={{ color: 'rgb(78, 147, 122)' }} /> : <StopOutlined style={{ color: 'red' }} />}
          </Row>
        );
      }
    },
    {
      title: 'Owner::Condition::Disposition',
      dataIndex: 'ocd',
      width: 30,
      render: (_: string, record): JSX.Element => {
        return (
          <div>
            {record.ownerId} :: {record.conditionId} :: {record.dispositionId}
          </div>
        );
      }
    },
    {
      title: 'Site',
      dataIndex: 'siteId',
      width: 20,
      render: (text: string): JSX.Element => {
        return <div>{text}</div>;
      }
    },
    {
      title: 'Warehouse',
      dataIndex: 'warehouseId',
      width: 20,
      render: (text: string): JSX.Element => {
        return <div>{text}</div>;
      }
    },
    {
      title: 'Quantity',
      dataIndex: 'qty',
      width: 20,
      align: 'right',
      render: (text: string, record): JSX.Element => {
        return <div style={styles.number}>{numberFormatter.format(Number(record.quantity))}</div>;
      }
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      width: 20,
      align: 'right',
      render: (text: string, record): JSX.Element => {
        return <div style={styles.number}>{currencyFormatter.format(Number(record.unitPrice))}</div>;
      }
    },
    {
      title: 'Ext. Price',
      dataIndex: 'extendedPrice',
      width: 20,
      align: 'right',
      render: (text: string, record): JSX.Element => {
        return <div style={styles.number}>{currencyFormatter.format(Number(record.quantity) * Number(record.unitPrice))}</div>;
      }
    },
    {
      title: 'Delivery Type',
      dataIndex: 'deliveryType',
      width: 20,
      render: (text: string): JSX.Element => {
        return <div>{text}</div>;
      }
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor',
      width: 20,
      render: (text: string, record): JSX.Element => {
        return <div>{record.vendorId}</div>;
      }
    },
    {
      title: 'Unit Cost',
      dataIndex: 'unitCost',
      width: 20,
      align: 'right',
      render: (text: string, record): JSX.Element => {
        return <div style={styles.number}>{currencyFormatter.format(Number(record.unitCost))}</div>;
      }
    },
    {
      title: 'Total Cost',
      dataIndex: 'totalCost',
      width: 20,
      align: 'right',
      fixed: 'right',
      render: (text: string, record): JSX.Element => {
        return <div style={styles.number}>{currencyFormatter.format(Number(record.quantity) * Number(record.unitCost))}</div>;
      }
    }
  ];

  /* ****************** Render ****************** */
  return (
    <Table
      columns={isPosted ? columnsPosted : columns}
      dataSource={values.lines.map((lineItem, index) => ({ key: index.toString(), ...lineItem }))}
      scroll={{ x: 2500 }}
      style={styles.table}
      pagination={false}
    />
  );
};
